import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutTemplate, Plus } from "lucide-react";
import { shiftApi } from "../../../api/resources";
import type { ShiftAdmin, ShiftQuery } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { Tabs } from "../../../components/Overlay";
import { Button } from "../../../components/ui";
import { ShiftCalendar } from "./ShiftCalendar";
import { ShiftDrawer } from "./ShiftDrawer";
import { ShiftFilters } from "./ShiftFilters";
import { ShiftFormModal } from "./ShiftFormModal";
import { ShiftTable } from "./ShiftTable";
import { TemplateRunModal } from "../templates/TemplateRunModal";

export default function AdminShifts() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "list";
  const [filters, setFilters] = useState<ShiftQuery>(() => ({ scope: params.get("scope") ?? undefined }));
  const [reload, setReload] = useState(0);
  const [openId, setOpenId] = useState<string | null>(params.get("shift"));
  const [creating, setCreating] = useState(false);
  const [fromTpl, setFromTpl] = useState(false);
  const [editing, setEditing] = useState<ShiftAdmin | null>(null);
  const [attention, setAttention] = useState<number | undefined>(undefined);
  const bump = () => setReload((r) => r + 1);

  useEffect(() => {
    shiftApi.list({ needs_attention: true }).then((r) => setAttention(r.length)).catch(() => setAttention(undefined));
  }, [reload]);

  const setTab = (t: string) => {
    const p = new URLSearchParams(params);
    p.set("tab", t);
    setParams(p, { replace: true });
  };

  return (
    <section data-testid="admin-shifts-page" className="animate-rise">
      <PageHeader title="Shift Assignments">
        <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={() => setFromTpl(true)} data-testid="shift-from-template-button"><LayoutTemplate className="h-4 w-4" /> From template</Button>
        <Button onClick={() => setCreating(true)} data-testid="shift-create-button"><Plus className="h-4 w-4" /> New assignment</Button>
        </div>
      </PageHeader>
      <Tabs active={tab} onChange={setTab} tabs={[{ key: "list", label: "List" }, { key: "calendar", label: "Calendar" }, { key: "attention", label: "Needs attention", count: attention }]} />
      {tab === "list" && (
        <>
          <ShiftFilters value={filters} onChange={setFilters} />
          <ShiftTable query={filters} reload={reload} onOpen={setOpenId} testId="shifts-table" />
        </>
      )}
      {tab === "calendar" && (
        <>
          <ShiftFilters value={filters} onChange={setFilters} showDates={false} />
          <ShiftCalendar filters={filters} reload={reload} onOpen={setOpenId} />
        </>
      )}
      {tab === "attention" && (
        <>
          <p className="mb-6 max-w-2xl text-sm text-zinc-400">Every unresolved ambiguity. Flags clear automatically when the condition is fixed, or an admin can acknowledge them with a note. Flagged shifts can't be confirmed.</p>
          <ShiftTable query={{ needs_attention: true }} reload={reload} onOpen={setOpenId} testId="attention-table" showFlags />
        </>
      )}
      <TemplateRunModal open={fromTpl} template={null} onClose={() => setFromTpl(false)} onDone={bump} onOpenShift={(id) => { setFromTpl(false); setOpenId(id); }} />
      <ShiftDrawer id={openId} reload={reload} onClose={() => setOpenId(null)} onChanged={bump} onEdit={setEditing} />
      <ShiftFormModal open={creating || !!editing} shift={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={bump} onOpen={setOpenId} />
    </section>
  );
}
